import { createFileRoute, Link } from "@tanstack/react-router";
import { Star, Quote, ShieldCheck } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { COMPANY } from "@/lib/company";
import { getActiveProvince } from "@/provinces/_registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildFaqSchema,
} from "@/lib/schema";
import { LongFormContent } from "@/components/LongFormContent";
import type { PageContent } from "@/content/site-pages";

interface Review {
  author: string;
  locale: string;
  rating: number;
  date: string; // ISO yyyy-mm-dd
  service: string;
  body: string;
}

function buildReviews(provinceTh: string, provinceEn: string): Review[] {
  return [
    {
      author: "James W.",
      locale: "🇦🇺 Australia",
      rating: 5,
      date: "2026-04-12",
      service: "NAATI Translation + Marriage in Australia",
      body: `Booked NAATI translation and consular legalization in ${provinceEn}. Documents were ready in 3 working days and accepted by Department of Home Affairs without any issues.`,
    },
    {
      author: "Anna K.",
      locale: "🇩🇪 Deutschland",
      rating: 5,
      date: "2026-03-28",
      service: "Notary Public + MFA + Embassy",
      body: `Sehr professionell. Die Beglaubigung für das deutsche Konsulat war perfekt vorbereitet. Sprachbarriere gab es nicht — das Team in ${provinceEn} spricht Englisch.`,
    },
    {
      author: "คุณวรรณภา ส.",
      locale: "🇹🇭 ไทย",
      rating: 5,
      date: "2026-03-10",
      service: "รับรองหนังสือเดินทาง + Power of Attorney",
      body: `ทีมงานใน${provinceTh}ดูแลดีมาก ตอบ LINE ภายใน 10 นาที นัดทำเอกสารที่บ้านพักได้ เซ็นต่อหน้าทนาย Notary จริง มีตราประทับครบ`,
    },
    {
      author: "Дмитрий П.",
      locale: "🇷🇺 Россия",
      rating: 5,
      date: "2026-02-21",
      service: "Apostille + Russian Consul Legalization",
      body: `Спасибо команде в ${provinceEn}! Сделали апостиль и перевод свидетельства о браке за 5 рабочих дней. Документы приняли в России без замечаний.`,
    },
    {
      author: "Marco F.",
      locale: "🇮🇹 Italia",
      rating: 5,
      date: "2026-02-05",
      service: "Yacht Crew Notary (Captain License + Sea Service)",
      body: `Notarized captain license and sea service record at Chalong marina. Came on-site to the boat. Fast, professional, accepted by Italian flag state.`,
    },
    {
      author: "Sarah L.",
      locale: "🇬🇧 United Kingdom",
      rating: 5,
      date: "2026-01-18",
      service: "Marriage Registration (Thai-British)",
      body: `Handled our entire marriage paperwork: Affidavit at British Embassy, MFA legalization, and registration at amphur — all in ${provinceEn}. Wonderful guidance throughout.`,
    },
    {
      author: "Kenji T.",
      locale: "🇯🇵 日本",
      rating: 5,
      date: "2025-12-22",
      service: "Power of Attorney + Japanese Translation",
      body: `${provinceEn}支店で委任状の認証と日本語翻訳をお願いしました。スタッフがとても丁寧で、領事館の要件をすべて満たしてくれました。`,
    },
    {
      author: "Pierre M.",
      locale: "🇫🇷 France",
      rating: 5,
      date: "2025-11-30",
      service: "DTV Visa Document Bundle",
      body: `Préparation complète du dossier DTV à ${provinceEn}: contrat, attestation bancaire, traduction certifiée. Visa obtenu en 12 jours.`,
    },
    {
      author: "Ahmed Z.",
      locale: "🇦🇪 UAE",
      rating: 5,
      date: "2025-11-14",
      service: "Property Purchase POA + Embassy Legalization",
      body: `Bought a condo in ${provinceEn} remotely using the POA they prepared. Land Office accepted on first try. Highly recommended for foreign property buyers.`,
    },
    {
      author: "Mei C.",
      locale: "🇨🇳 中国",
      rating: 5,
      date: "2025-10-09",
      service: "Notary + China Consul Legalization",
      body: `${provinceEn}的团队帮我办理了出生证明和无犯罪记录的领事认证，全程沟通顺畅，文件在中国使用没有任何问题。`,
    },
  ];
}

export const Route = createFileRoute("/reviews")({
  component: ReviewsPage,
  loader: async (): Promise<{ content: PageContent }> => {
    const { REVIEWS_CONTENT } = await import("@/content/site-pages");
    return { content: REVIEWS_CONTENT };
  },
  head: ({ loaderData }) => {
    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const title = `รีวิวลูกค้า Notary ${config.nameEn} — เสียงจากผู้ใช้บริการจริง`;
    const description = `เสียงจากลูกค้า Expat, Yacht Crew และคู่รักไทย–ต่างชาติใน${config.nameTh} เกี่ยวกับงาน Notary Public, NAATI, รับรองสถานทูต และจดทะเบียนสมรส`;
    const head = pageHeadForProvince({ title, description, path: "/reviews" });
    const reviews = buildReviews(config.nameTh, config.nameEn);
    return {
      ...head,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildOrganizationSchema()) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "รีวิวลูกค้า", url: `${site}/reviews` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": `${site}/reviews#page`,
            url: `${site}/reviews`,
            name: title,
            description,
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: reviews.length,
              itemListElement: reviews.map((r, idx) => ({
                "@type": "ListItem",
                position: idx + 1,
                name: `${r.author} — ${r.service}`,
                item: {
                  "@type": "CreativeWork",
                  headline: r.service,
                  author: { "@type": "Person", name: r.author },
                  datePublished: r.date,
                  text: r.body,
                  inLanguage: "th-TH",
                  about: { "@id": `${site}/#organization` },
                },
              })),
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildFaqSchema(loaderData?.content.faqs ?? [])),
        },
      ],
    };
  },
});

function ReviewsPage() {
  const { content } = Route.useLoaderData();
  const { config } = getActiveProvince();
  const reviews = buildReviews(config.nameTh, config.nameEn);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-14 text-center">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold text-gold">
              <ShieldCheck className="h-3.5 w-3.5" /> Verified Reviews · {config.nameEn}
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight md:text-5xl">
              รีวิวจากลูกค้าจริงใน{config.nameTh}
            </h1>
            <p className="mt-5 text-sm font-semibold text-muted-foreground">
              {COMPANY.trustLine}
            </p>
            <p className="mx-auto mt-4 max-w-2xl text-sm text-muted-foreground">
              ลูกค้ามากกว่า 40 สัญชาติเลือก Thai Notary {config.nameEn} เพราะตอบเร็ว เอกสารผ่านตั้งแต่ครั้งแรก
            </p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-6xl gap-5 px-4 py-14 md:grid-cols-2">
            {reviews.map((r) => (
              <article key={r.author + r.date} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                <Quote className="mb-3 h-6 w-6 text-gold" />
                <p className="text-sm leading-relaxed text-foreground">{r.body}</p>
                <div className="mt-4 flex items-center gap-1">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-gold text-gold" />
                  ))}
                </div>
                <div className="mt-3 border-t border-border pt-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-bold">{r.author}</div>
                    <div className="text-xs text-muted-foreground">{r.locale}</div>
                  </div>
                  <div className="text-xs text-muted-foreground">{r.service}</div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {new Date(r.date).toLocaleDateString("th-TH", { year: "numeric", month: "long", day: "numeric" })}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <LongFormContent content={content} />

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-12 text-center">
            <h2 className="text-2xl font-bold md:text-3xl">เป็นลูกค้ารายต่อไปใน{config.nameTh}</h2>
            <p className="mt-3 text-sm opacity-90">ปรึกษาฟรี ตอบกลับ {COMPANY.responseTime}</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a href={`tel:${COMPANY.phoneIntl}`} className="rounded-md bg-gold px-5 py-3 text-sm font-bold text-gold-foreground">
                📞 {COMPANY.phone}
              </a>
              <Link to="/contact" className="rounded-md border border-primary-foreground/40 px-5 py-3 text-sm font-bold">
                ติดต่อเรา
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}